import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format, differenceInDays } from 'date-fns';
import axios from 'axios';
import { 
  Search, Calendar, MapPin, Filter, Users, Fuel, Settings2, 
  ChevronRight, X, SlidersHorizontal 
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehiclesPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Filter state from URL params
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [pickupLocation, setPickupLocation] = useState(searchParams.get('pickupLocation') || '');
  const [returnLocation, setReturnLocation] = useState(searchParams.get('returnLocation') || '');
  
  // Date range state
  const [dateRange, setDateRange] = useState({
    from: searchParams.get('pickupDate') ? new Date(searchParams.get('pickupDate')) : null,
    to: searchParams.get('returnDate') ? new Date(searchParams.get('returnDate')) : null
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [locRes, catRes] = await Promise.all([
          axios.get(`${API_URL}/api/locations`),
          axios.get(`${API_URL}/api/vehicles/categories/list`)
        ]);
        setLocations(locRes.data);
        setCategories(catRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      try {
        if (dateRange.from && dateRange.to) {
          // Use search endpoint for availability check
          const response = await axios.post(`${API_URL}/api/vehicles/search`, null, {
            params: {
              pickup_date: format(dateRange.from, 'yyyy-MM-dd'),
              return_date: format(dateRange.to, 'yyyy-MM-dd'),
              category: category && category !== 'all' ? category : undefined,
              pickup_location_id: pickupLocation || undefined
            }
          });
          setVehicles(response.data);
        } else {
          // Just get all vehicles with optional category filter
          let url = `${API_URL}/api/vehicles?available=true`;
          if (category && category !== 'all') {
            url += `&category=${category}`;
          }
          const response = await axios.get(url);
          setVehicles(response.data);
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, [category, dateRange, pickupLocation]);

  const handleDateSelect = (range) => {
    setDateRange(range || { from: null, to: null });
    if (range?.from && range?.to) {
      setTimeout(() => setCalendarOpen(false), 300);
    }
  };

  const rentalDays = dateRange.from && dateRange.to 
    ? differenceInDays(dateRange.to, dateRange.from) 
    : 0;

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (category && category !== 'all') params.set('category', category);
    if (pickupLocation) params.set('pickupLocation', pickupLocation);
    if (returnLocation) params.set('returnLocation', returnLocation);
    if (dateRange.from) params.set('pickupDate', format(dateRange.from, 'yyyy-MM-dd'));
    if (dateRange.to) params.set('returnDate', format(dateRange.to, 'yyyy-MM-dd'));
    setSearchParams(params);
  };

  const clearFilters = () => {
    setCategory('');
    setPickupLocation('');
    setReturnLocation('');
    setDateRange({ from: null, to: null });
    setSearchParams({});
  };

  const handleVehicleClick = (vehicleId) => {
    const params = new URLSearchParams();
    if (pickupLocation) params.set('pickupLocation', pickupLocation);
    if (returnLocation) params.set('returnLocation', returnLocation);
    if (dateRange.from) params.set('pickupDate', format(dateRange.from, 'yyyy-MM-dd'));
    if (dateRange.to) params.set('returnDate', format(dateRange.to, 'yyyy-MM-dd'));
    navigate(`/vehicles/${vehicleId}?${params.toString()}`);
  };

  const getLocalizedName = (item) => {
    return i18n.language === 'fr' ? item.name_fr : item.name_en;
  };

  const hasFilters = category || pickupLocation || returnLocation || dateRange.from || dateRange.to;

  return (
    <Layout>
      {/* Header */}
      <section className="py-12 border-b border-gray-200" data-testid="vehicles-header">
        <div className="section-container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary font-medium mb-2">
                {t('nav.fleet')}
              </p>
              <h1 className="font-serif text-4xl md:text-5xl font-light text-[#181B1F]">
                {t('hero.searchTitle')}
              </h1>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="btn-outline lg:hidden"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {/* Filters */}
          <div className={cn(
            "glass-surface p-6 space-y-4 lg:block",
            showFilters ? "block" : "hidden lg:block"
          )}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Category */}
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="input-default" data-testid="filter-category">
                  <SelectValue placeholder={t('search.category')} />
                </SelectTrigger>
                <SelectContent className="bg-white shadow-lg border-gray-200">
                  <SelectItem value="all" className="text-[#181B1F]">
                    {t('search.allCategories')}
                  </SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} className="text-[#181B1F]">
                      {getLocalizedName(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Pickup Location */}
              <Select value={pickupLocation} onValueChange={setPickupLocation}>
                <SelectTrigger className="input-default" data-testid="filter-pickup">
                  <SelectValue placeholder={t('search.pickupLocation')} />
                </SelectTrigger>
                <SelectContent className="bg-white shadow-lg border-gray-200">
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id} className="text-[#181B1F]">
                      {getLocalizedName(loc)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date Range Picker */}
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left input-default border",
                      !dateRange.from && "text-gray-400"
                    )}
                    data-testid="filter-date-range"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <span className="truncate">
                          {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d")}
                          {rentalDays > 0 && ` (${rentalDays}d)`}
                        </span>
                      ) : (
                        format(dateRange.from, "PP")
                      )
                    ) : (
                      t('search.selectDates')
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white shadow-lg border-gray-200" align="start">
                  <CalendarComponent
                    mode="range"
                    selected={dateRange}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Search Button */}
              <Button onClick={handleSearch} className="btn-primary" data-testid="apply-filters-btn">
                <Search className="w-4 h-4 mr-2" />
                {t('search.searchBtn')}
              </Button>
            </div>

            {hasFilters && (
              <div className="flex items-center gap-2 pt-2">
                <span className="text-gray-400 text-sm">Active filters:</span>
                {category && category !== 'all' && (
                  <Badge variant="secondary" className="bg-white/10 text-[#181B1F]">
                    {categories.find(c => c.id === category)?.name_en || category}
                  </Badge>
                )}
                {dateRange.from && dateRange.to && (
                  <Badge variant="secondary" className="bg-white/10 text-[#181B1F]">
                    {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d")}
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-400 hover:text-[#181B1F]"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear all
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Vehicles Grid */}
      <section className="py-12" data-testid="vehicles-grid">
        <div className="section-container">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-500">{t('common.loading')}</p>
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-2xl font-semibold">
                {i18n.language === 'fr' ? 'Complet' : 'Sold Out'}
              </p>
              <p className="text-gray-400 mt-2">
                {i18n.language === 'fr' 
                  ? 'Aucun véhicule disponible pour ces dates' 
                  : 'No vehicles available for these dates'}
              </p>
            </div>
          ) : (
            <>
              <p className="text-gray-400 mb-6">
                {vehicles.length} {i18n.language === 'fr' ? 'véhicules disponibles' : 'vehicles available'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {vehicles.map((vehicle, index) => (
                  <div
                    key={vehicle.id}
                    className="card-vehicle group cursor-pointer"
                    onClick={() => handleVehicleClick(vehicle.id)}
                    data-testid={`vehicle-card-${vehicle.id}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img
                        src={vehicle.images[0] || 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=600'}
                        alt={vehicle.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-primary text-[#181B1F]">
                          {i18n.language === 'fr' ? vehicle.category_name_fr : vehicle.category_name_en}
                        </Badge>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-serif text-2xl text-[#181B1F]">{vehicle.name}</h3>
                          <p className="text-gray-400 text-sm">{vehicle.brand} • {vehicle.year}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-gray-400 text-sm">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" strokeWidth={1.5} />
                          {vehicle.seats} {t('vehicle.seats')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Settings2 className="w-4 h-4" strokeWidth={1.5} />
                          {vehicle.transmission === 'automatic' ? t('vehicle.automatic') : t('vehicle.manual')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Fuel className="w-4 h-4" strokeWidth={1.5} />
                          {vehicle.fuel_type}
                        </span>
                      </div>

                      <div className="flex items-end justify-between pt-4 border-t border-gray-200">
                        <div>
                          <span className="text-gray-400 text-xs">{t('vehicle.from')}</span>
                          <p className="text-2xl text-[#181B1F] font-light">
                            CHF {vehicle.calculated_daily_rate || vehicle.base_daily_rate}
                            <span className="text-gray-400 text-sm">{t('vehicle.perDay')}</span>
                          </p>
                          {vehicle.rental_days && vehicle.rental_days > 0 && (
                            <p className="text-primary text-sm">
                              {vehicle.rental_days} {t('booking.days')} = CHF {vehicle.calculated_total_price?.toFixed(2)}
                            </p>
                          )}
                        </div>
                        <Button className="btn-primary h-10 px-4">
                          {t('vehicle.bookNow')}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default VehiclesPage;
